// src/components/blog/ShareButtons.tsx
'use client';

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaShareAlt, FaFacebookF, FaTwitter, FaLinkedinIn } from 'react-icons/fa';

interface BlogPostData {
  title: string;
  content: string;
  shareCount: number;
  excerpt?: string;
  slug: string;
}

interface ShareProps {
  post: BlogPostData;
  ogImageUrl: string;
}

export default function ShareButtons({ post, ogImageUrl }: ShareProps) {
  const [shareCount, setShareCount] = useState<number>(post.shareCount ?? 0);

  useEffect(() => {
    const fetchShareCount = async () => {
      try {
        const res = await fetch(`/api/blog/${post.slug}/share`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        setShareCount(data.shares ?? post.shareCount ?? 0);
      } catch {
        console.log('Failed to fetch share count for', post.slug);
      }
    };
    fetchShareCount();
  }, [post.slug, post.shareCount]);

  const incrementShare = async (platform?: string) => {
    try {
      const res = await fetch(`/api/blog/${post.slug}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to share');
      setShareCount(data.shares ?? shareCount);
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleNativeShare = async () => {
    const url = `${window.location.origin}/blog/${post.slug}`;
    setShareCount((c) => c + 1);
    await incrementShare('native');

    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url }).catch(() => null);
      } else {
        await navigator.clipboard.writeText(url);
        toast.info('Link copied to clipboard!');
      }
      toast.success('Post shared!');
    } catch {
      setShareCount((c) => Math.max(0, c - 1));
      toast.error('Failed to share post');
    }
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent(`${window.location.origin}/blog/${post.slug}`);
    const image = encodeURIComponent(ogImageUrl);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}&picture=${image}`,
      '_blank',
      'width=600,height=500'
    );
    incrementShare('facebook');
  };

  const shareToTwitter = () => {
    const url = encodeURIComponent(`${window.location.origin}/blog/${post.slug}`);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank', 'width=600,height=400');
    incrementShare('twitter');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent(`${window.location.origin}/blog/${post.slug}`);
    const title = encodeURIComponent(post.title);
    const summary = encodeURIComponent(post.excerpt ?? '');
    const source = encodeURIComponent(window.location.origin);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}&source=${source}`,
      '_blank',
      'width=600,height=500'
    );
    incrementShare('linkedin');
  };

  return (
    <div className="flex items-center gap-2">
      <button onClick={handleNativeShare} className="flex items-center gap-1 px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition" title="Share / Copy Link">
        <FaShareAlt /> {shareCount}
      </button>
      <button onClick={shareToFacebook} className="p-1 rounded bg-blue-600 text-white hover:bg-blue-700 transition" title="Facebook">
        <FaFacebookF size={14} />
      </button>
      <button onClick={shareToTwitter} className="p-1 rounded bg-blue-400 text-white hover:bg-blue-500 transition" title="Twitter">
        <FaTwitter size={14} />
      </button>
      <button onClick={shareToLinkedIn} className="p-1 rounded bg-blue-800 text-white hover:bg-blue-900 transition" title="LinkedIn">
        <FaLinkedinIn size={14} />
      </button>
    </div>
  );
}
